/* =============================================================================
   config.js - all editable master data of the plate configurator.
   No global state, no localStorage: constants plus pure getters only.
   Must be loaded BEFORE validate.js and plate.js.
   User-facing strings (labels) stay German on purpose.
   ========================================================================== */
var PlateConfig = (function () {
  'use strict';

  /* --- Sizes in mm. Edit here, everything else follows. ---------------------
     twoLine    : true -> city code on line 1, letters + digits on line 2.
     parking    : true -> a parking sign, not a registration plate. It carries
                  one free line of text instead of a Unterscheidungszeichen and
                  an Erkennungsnummer, and the blue band shows a P instead of
                  the stars. Preview only for now - there is no product behind
                  it and nothing reaches the cart.
     maxLetters / maxDigits : how many characters the Erkennungsnummer holds on
                  this size. Only the small plates need it - the wide ones take
                  what fits. Typing more is trimmed with a notice, exactly as
                  the single-line sizes have always done. This is a limit of the
                  plate, not of the law: validate.js stays permissive.
     maxLettersSuffix / maxDigitsSuffix : the limits on an E or H plate, where
                  the suffix takes a character's worth of room off the bottom
                  row. Only the motorcycle sizes are narrow enough to care. On
                  those the preview also lets letters and digits take each
                  other's room: both limits are upper bounds, and what is left
                  depends on what fits next to the other field.
     lightBike  : true -> the Leichtkraftrad arrangement: the city code and the
                  seal group share the top row, the number sits on the bottom
                  row and is spread the way justify-content: space-around
                  spreads it. Two rows like the two-line car sizes, but with
                  the reference's own paddings, field widths and font sizes -
                  see the LKR block in sections/kfz-konfigurator.liquid.
                  The Traktor plate is drawn from the same flag: the reference
                  gives both the same two sizes and, measured box by box, the
                  same layout down to the last decimal - only the class on the
                  plate differs (numberplate-tractor instead of
                  numberplate-light-motorcycle).
     previewWidth : the width the preview draws, where that is not the width
                  the size is sold under. Only the 255 mm plate needs it: the
                  reference draws that option 250 mm wide (25em at an 11 px
                  base), so a 1:1 rebuild has to draw 250 as well. Without the
                  field the preview uses w.
     sealBelowCity : true -> the seal gets a row of its own, centred under the
                  city code, and the number moves down to a third row. The
                  motorcycle plates are nearly square, so there is room for it.
                  Without the flag the seal sits beside the city code, which is
                  what the two-line car sizes do.
     charHeight : cap height of a character on this size. Smaller plates carry
                  smaller characters, so this belongs to the size and not to
                  METRICS - the preview must show the height the customer
                  actually receives, not a scaled-down 75 mm line.
     This is the full catalogue. Which of them a page actually offers is
     decided per section - see sizesFrom() below.

     TODO: the charHeight values below are DRAFTS. Confirm every one of them
     with the owner before going live - a wrong character height means the
     preview misrepresents the product. Open point: the 200 mm motorcycle
     sizes are drawn with 49 mm characters here.                              */
  var SIZES = [
    { id: '520x110', w: 520, h: 110, twoLine: false, maxLetters: 2, maxDigits: 4, charHeight: 75, label: '520 × 110 mm' },
    { id: '520x114', w: 520, h: 114, twoLine: false, parking: true, charHeight: 60, maxText: 9, label: '520 × 114 mm' },
    { id: '480x110', w: 480, h: 110, twoLine: false, maxLetters: 2, maxDigits: 3, charHeight: 75, label: '480 × 110 mm' },
    { id: '460x110', w: 460, h: 110, twoLine: false, maxLetters: 2, maxDigits: 3, charHeight: 75, label: '460 × 110 mm' },
    { id: '420x110', w: 420, h: 110, twoLine: false, maxLetters: 2, maxDigits: 2, charHeight: 75, label: '420 × 110 mm' },
    { id: '400x110', w: 400, h: 110, twoLine: false, maxLetters: 2, maxDigits: 1, charHeight: 75, label: '400 × 110 mm' },
    { id: '340x200', w: 340, h: 200, twoLine: true,  maxLetters: 2, maxDigits: 4, charHeight: 75, label: 'Zweizeilig (340 × 200 mm)' },
    { id: '320x200', w: 320, h: 200, twoLine: true,  maxLetters: 2, maxDigits: 4, charHeight: 75, label: 'Zweizeilig (320 × 200 mm)' },
    { id: '280x200', w: 280, h: 200, twoLine: true,  maxLetters: 2, maxDigits: 4, charHeight: 75, label: 'Zweizeilig (280 × 200 mm)' },
    { id: '255x130', w: 255, h: 130, twoLine: true,  lightBike: true, previewWidth: 250, maxLetters: 2, maxDigits: 3, charHeight: 49, label: '255 × 130 mm' },
    { id: '240x130', w: 240, h: 130, twoLine: true,  lightBike: true, maxLetters: 2, maxDigits: 3, charHeight: 49, label: '240 × 130 mm' },
    { id: '220x200', w: 220, h: 200, twoLine: true,  sealBelowCity: true, maxLetters: 2, maxDigits: 4, maxLettersSuffix: 2, maxDigitsSuffix: 3, charHeight: 49, label: '220 × 200 mm' },
    { id: '200x200', w: 200, h: 200, twoLine: true,  sealBelowCity: true, maxLetters: 2, maxDigits: 3, maxLettersSuffix: 2, maxDigitsSuffix: 2, charHeight: 49, label: '200 × 200 mm' },
    { id: '180x200', w: 180, h: 200, twoLine: true,  sealBelowCity: true, maxLetters: 2, maxDigits: 3, maxLettersSuffix: 1, maxDigitsSuffix: 1, charHeight: 49, label: '180 × 200 mm' }
  ];

  /* --- Which sizes a page offers -------------------------------------------
     Not every size belongs on every product: a car plate page must not offer
     motorcycle sizes. The section passes the ids it wants, in the order it
     wants them; sizesFrom() keeps that order and drops anything unknown, so a
     typo in the theme editor cannot empty the dropdown.                       */
  function sizesFrom(ids) {
    var wanted = [];
    if (typeof ids === 'string') { ids = ids.split(','); }
    if (Object.prototype.toString.call(ids) !== '[object Array]') { return SIZES; }
    for (var i = 0; i < ids.length; i++) {
      var found = getSize(String(ids[i]).trim());
      if (found) { wanted.push(found); }
    }
    return wanted.length ? wanted : SIZES;
  }

  /* --- Plate types ----------------------------------------------------------
     suffix          : character appended after the digits ('E' / 'H')
     sealAfterDigits : true  -> seal / badge area sits after the digits
                       false -> seal sits right after the city code
     isSeason        : true  -> season months apply                            */
  var TYPES = [
    { id: 'standard', label: 'Standard',      suffix: null, sealAfterDigits: false, isSeason: false },
    { id: 'e',        label: 'E-Kennzeichen', suffix: 'E',  sealAfterDigits: true,  isSeason: false },
    { id: 'h',        label: 'Oldtimer (H)',  suffix: 'H',  sealAfterDigits: true,  isSeason: false },
    { id: 'season',   label: 'Saison',        suffix: null, sealAfterDigits: false, isSeason: true }
  ];

  var MONTHS = [
    { value: '01', label: '01 – Januar' },    { value: '02', label: '02 – Februar' },
    { value: '03', label: '03 – März' },      { value: '04', label: '04 – April' },
    { value: '05', label: '05 – Mai' },       { value: '06', label: '06 – Juni' },
    { value: '07', label: '07 – Juli' },      { value: '08', label: '08 – August' },
    { value: '09', label: '09 – September' }, { value: '10', label: '10 – Oktober' },
    { value: '11', label: '11 – November' },  { value: '12', label: '12 – Dezember' }
  ];

  var QUANTITIES = [
    { value: 1, label: '1 Kennzeichen' },
    { value: 2, label: '2 Kennzeichen (Paar)' }
  ];

  /* --- Rules --------------------------------------------------------------- */
  var LIMITS = {
    cityCode:   { min: 1, max: 3 },  // Unterscheidungszeichen
    letters:    { min: 1, max: 2 },  // Erkennungsnummer, letter part
    digits:     { min: 1, max: 4 },  // Erkennungsnummer, digit part
    totalChars: 8,                   // cityCode + letters + digits
    season:     { minMonths: 2, maxMonths: 11 } // inclusive duration, wrap-around allowed
  };

  // Letter combinations not allowed for the Erkennungsnummer.
  var FORBIDDEN_COMBOS = ['SS', 'SA', 'HJ', 'KZ', 'NS'];

  /* --- Geometry for the SVG preview, all values in mm -----------------------
     Read by plate.js. Roughly modelled on the real dimensions and meant to be
     tweaked here rather than in the drawing code.                             */
  var METRICS = {
    // Plate body
    euroWidth:         44,    // width of the blue EU band
    borderInset:       0,     // 0 = the frame sits on the plate edge and is the
                              // outline of the plate; raise it for a margin
                              // between plate edge and frame
    borderWidth:       4,     // stroke width of the embossed border
    cornerRadius:      10,    // plate corner radius
    contentPadding:    8,     // free space around the content, right of the EU band

    // Characters. Widths and the character gap are given for charRefHeight and
    // scale proportionally with the charHeight of the selected size, so a 49 mm
    // plate gets 49 mm characters at the correct aspect ratio.
    charRefHeight:     75,    // cap height the two widths below refer to
    charWidth:         47.5,  // advance width of a letter at charRefHeight
    digitWidth:        44.5,  // advance width of a digit at charRefHeight
    capHeightRatio:    0.72,  // cap height / font size of the placeholder font
    charGap:           4,     // gap between two characters at charRefHeight
    groupGap:          10,    // gap between blocks (city code, seal, letters, ...)
    lineGap:           8,     // vertical gap between the two lines of a two-line plate

    // Parking sign. Both values are fractions of the plate height, read off the
    // reference's p.svg (viewBox 47.5 x 99.4, the P running from y 23.79 to
    // 75.61 and therefore centred).
    parkingBandRatio:   0.478,  // width of the blue band / plate height
    parkingLetterRatio: 0.52,   // cap height of the P / plate height

    // Seal (Siegel)
    sealDiameter:       45,    // diameter of the seal / badge area
    sealStrokeWidth:    1.5,   // placeholder outline
    sealDashArray:      '6 4', // placeholder outline
    // The icon is the whole seal badge. sealIconRatio insets it inside the
    // reserved circle, sealOffsetY shifts it off the character centre line
    // (positive = down), the way the stamp sits on a real plate.
    sealIconRatio:      0.68,  // state icon size, as a fraction of the diameter
    sealOffsetY:        9,     // vertical offset from the line centre, mm

    // Season oval
    seasonWidth:       36,
    seasonHeight:      80,
    seasonStrokeWidth: 2,
    seasonTextHeight:  24,    // cap height of one month inside the oval

    // EU band contents
    starCircleRadius:  11,    // radius of the circle the 12 stars sit on
    starRadius:        3.4,   // outer radius of a single star
    starsCenterRatio:  0.35,  // star circle centre, as a fraction of plate height
    euroLetterHeight:  14,    // cap height of the "D"
    euroLetterRatio:   0.76   // "D" centre, as a fraction of plate height
  };

  /* --- Asset location -------------------------------------------------------
     STATES[x].iconUrl in data/districts.js is a bare filename, because Shopify
     stores every asset flat in assets/ with no subfolders. This prefix is the
     only thing that differs between local dev and the shop.

     The Shopify section overwrites PlateConfig.ASSET_BASE once, before init,
     with the base Liquid resolves:
       PlateConfig.ASSET_BASE = "{{ 'seal-bw.webp' | asset_url | split: 'seal-bw.webp' | first }}";
     which yields the CDN path all the other seal-*.webp files share. It is
     read through the exported object on every call, so setting it from
     outside actually takes effect.                                            */
  var ASSET_BASE = './assets/';

  // Bare filename -> usable URL. Empty in, empty out: callers use that to fall
  // back to the placeholder instead of requesting a broken image.
  function assetUrl(fileName) {
    if (!fileName) { return ''; }
    return (api.ASSET_BASE || '') + fileName;
  }

  /* --- Colours of the preview. Theme the plate here, not in plate.js. ------ */
  var COLORS = {
    plate:       '#ffffff',  // plate body
    border:      '#111111',  // embossed frame
    text:        '#111111',  // characters and the season oval
    euro:        '#003399',  // EU band
    star:        '#ffcc00',  // the 12 stars
    euroText:    '#ffffff',  // the "D"
    sealFill:    '#e8eaed',  // seal placeholder fill
    sealOutline: '#9aa0a6'   // seal placeholder dashed outline
  };

  /* --- Getters (pure) ------------------------------------------------------ */
  function getSize(id) {
    for (var i = 0; i < SIZES.length; i++) { if (SIZES[i].id === id) { return SIZES[i]; } }
    return null;
  }

  function getType(id) {
    for (var i = 0; i < TYPES.length; i++) { if (TYPES[i].id === id) { return TYPES[i]; } }
    return null;
  }

  function defaults() {
    return {
      cityCode: '', letters: '', digits: '',
      sizeId: SIZES[0].id,
      type: TYPES[0].id,
      seasonFrom: '03',
      seasonTo: '10',
      // Most cars need two plates, front and rear, so that is the default.
      quantity: 2
    };
  }

  // Held in a variable so assetUrl() can read ASSET_BASE off the exported
  // object: the host page sets it once before init, and every later call sees
  // the new value.
  var api = {
    SIZES: SIZES,
    TYPES: TYPES,
    MONTHS: MONTHS,
    QUANTITIES: QUANTITIES,
    LIMITS: LIMITS,
    FORBIDDEN_COMBOS: FORBIDDEN_COMBOS,
    METRICS: METRICS,
    COLORS: COLORS,
    ASSET_BASE: ASSET_BASE,
    assetUrl: assetUrl,
    getSize: getSize,
    sizesFrom: sizesFrom,
    getType: getType,
    defaults: defaults
  };

  return api;
})();
